import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Leuchtturm Conchas: Wie man hinaufkommt und der 360°-Blick auf Ilha do Mel',
  excerpt: 'Vollständiger Leitfaden zur Besteigung des Leuchtturms Farol das Conchas auf Ilha do Mel. Wanderweg, Öffnungszeiten, Eintrittspreis und die beste Zeit, um den Sonnenuntergang vom Gipfel der Insel aus zu beobachten.',
  publishedAt: '2026-04-24',
  author: 'Guia Ilha do Mel',
  tags: ['farol', 'trilha', 'passeio', 'vista-panoramica'],
  coverImage: '/images/praia-do-farol-hero.jpg',
  readingTime: '6 min',
};

# Leuchtturm Conchas: Aufstieg und 360°-Aussicht

Der **Leuchtturm Conchas** ist das bekannteste Postkartenmotiv der Ilha do Mel (Honiginsel). Er thront auf dem Morro do Farol (Leuchtturmhügel) und bietet einen der spektakulärsten Ausblicke auf die Küste von Paraná – Meer, Atlantischer Regenwald und Dörfer, alles von einem einzigen Aussichtspunkt aus.

In diesem Leitfaden erklären wir **wie man dorthin gelangt**, **wie viel es kostet**, **was man beim Aufstieg erwarten kann** und die **beste Zeit** für den Aufstieg.

---

Wo befindet sich der Leuchtturm von Conchas?

Der Leuchtturm befindet sich auf dem **Morro do Farol** (Leuchtturmhügel) im Zentrum der Insel, zwischen dem Strand Farol und dem Strand Praia Grande. Er ist über einen markierten Wanderweg erreichbar, der am Fuße des Hügels beginnt.

| Informationen | Details |

| ------------------- | -------------------------------- |

| **Ort** | Morro do Farol, Ilha do Mel — PR |

| **Höhe** | 150 Meter über dem Meeresspiegel |

| **Zugang** | Schotterweg |

| **Aufstiegszeit** | 15–20 Minuten (gemütliches Tempo) |

| **Schwierigkeitsgrad** | Leicht bis mittel |

---

Wie man zum Ausgangspunkt des Wanderwegs gelangt

Abfahrt von Nova Brasília

1. Folgen Sie dem Hauptweg nach Süden (Richtung Encantadas).
2. Nach etwa 10 Minuten Fußweg sehen Sie links ein Schild mit der Aufschrift „Leuchtturm“.
3. Der Leuchtturmweg führt den Hang hinauf.

Abreise von Encantadas

1. Folgen Sie dem Hauptweg nach Norden (Richtung Nova Brasília).
2. Das Leuchtturmschild befindet sich auf der rechten Seite, nachdem Sie den Strand Praia do Farol (Leuchtturmstrand) passiert haben.
3. Gesamtstrecke: ca. 25–30 Minuten Fußweg vom Pier Encantadas.

---

Der Wanderweg: Was Sie erwartet

Der Aufstieg ist **kurz, aber stellenweise steil**. Besondere Ausrüstung ist nicht erforderlich, einige Vorsichtsmaßnahmen sind jedoch wichtig:

- **Schuhwerk:** Tragen Sie Turnschuhe mit rutschfester Sohle. Die Felsen können rutschig sein, besonders nach Regen.

- **Wasser:** Bringen Sie eine kleine Flasche mit. Oben gibt es keine Verkaufsstellen.

- **Mücken:** Insektenschutzmittel ist in den dichten Waldgebieten hilfreich.

- **Regenschirm oder Regenjacke:** Das Wetter auf der Insel kann schnell umschlagen.

**Einheimischer Tipp:** Steigen Sie am späten Nachmittag (eine Stunde vor Sonnenuntergang) hinauf. Das goldene Licht über dem Meer ist einfach traumhaft – und die kühleren Temperaturen machen den Aufstieg angenehmer.

---

Ganz oben: die 360°-Ansicht

Oben angekommen, erwartet Sie Folgendes:

- Der **historische Leuchtturm** (erbaut 1872, noch in Betrieb)

- Ein **Aussichtsbereich** mit Sicherheitsgeländer

- Informationstafeln zur Geschichte des Leuchtturms und der Insel

Was von jeder Seite aus zu sehen ist:

| Richtung | Aussicht |

| --------- | -------------------------------------------------- |

| **Osten** | Atlantischer Ozean und der endlose Horizont |

| **Süden** | Strand von Encantadas und das Festland im Hintergrund |

| **Westen** | Bucht von Paranaguá, Mangroven und die Serra do Mar |

| **Norden** | Praia Grande, Nova Brasília und die Küstenstrände |

An klaren Tagen kann man den **Hafen von Paranaguá** und sogar die Stadt Paranaguá in der Ferne sehen.

---

Fahrpläne und Preise

| Artikel | Details |

| ---------------------------- | -------------------------------------- |

| **Öffnungszeiten** | 9:00 bis 17:00 Uhr (täglich) |

| **Eintritt** | Frei |

| **Beste Besuchszeit** | 16:30–17:00 Uhr (Sonnenuntergang) |

| **Hinweise** | Das Besteigen der Leuchtturmanlagen ist verboten.

**Achtung:** Der Leuchtturm schließt aus Sicherheitsgründen bei Einbruch der Dunkelheit. Bitte bleiben Sie nach Schließung nicht mehr oben – der Abstieg im Dunkeln ist gefährlich.

---

## Fotos: So fangen Sie die Aussicht ein

- **Weitwinkelobjektiv** (16–24 mm) erfasst die gesamte Aussicht.

- **Goldene Stunde** (1 Stunde vor Sonnenuntergang) bietet warmes Licht und lange Schatten.

- **Leichtes Stativ** eignet sich für Langzeitbelichtungen des Meeres in der Abenddämmerung.

- **Drohnen:** Überflüge sind erlaubt, jedoch ist auf starken Wind zu achten.

---

Besuchsregeln

- Bitte hinterlassen Sie keinen Müll auf dem Wanderweg oder oben.

Bitte beschädigen Sie die Leuchtturmanlagen nicht.

Bitte respektieren Sie die Stille – es ist ein Ort der Besinnung.

Haustiere sind auf dem Wanderweg erlaubt, müssen aber an der Leine geführt werden.

---

Kombinieren Sie die Tour mit anderen Touren.

Der Leuchtturm Farol das Conchas liegt genau zwischen den beiden Dörfern. Verbinden Sie Ihren Besuch mit:

- **Farol Beach** – Strand direkt unterhalb des Hügels, ideal zum Entspannen nach dem Aufstieg

- **Verzauberter Pfad** – Fortsetzung des Hauptweges zum südlichen Dorf

- **Großer Strand** – der größte Sandstrand der Insel, 10 Gehminuten entfernt

<MapsWidget query="Farol das Conchas Ilha do Mel" />

---

Möchten Sie eine komplette Reiseroute für Ilha do Mel planen? Unser [vollständiger Reiseführer für Ilha do Mel](/guide-complete) enthält Wanderwege, Strände, Restaurants und Tipps von Einheimischen, damit Sie jeden Winkel der Insel genießen können.